import { PaginationType } from '@data/types';
import useQuestionnaireStore from '@store/questionnaire';
import classNames from 'classnames';
import React from 'react';
import { hash } from 'utilities/helper';

import Orb from './Orb';
import PaginationItem from './PaginationItem';

interface Props extends PaginationType {
  as?: keyof JSX.IntrinsicElements;
}

const Pagination: React.FC<Props> = ({ as: Component = 'div', pillars }) => {
  const {
    pillarsLengthStore,
    pillarIndexStore,
    setPillarIndexStore,
    questionIndexStore,
    setQuestionIndexStore,
  } = useQuestionnaireStore();

  const firstPillar = pillarIndexStore === 0;

  // Current Pillar
  const currentPillar = pillars[pillarIndexStore];

  // Questions
  const { questions = [] } = currentPillar;
  const questionsLength = questions.length;
  const currentQuestion = questionIndexStore + 1;

  return (
    <Component className="md:absolute md:inset-0 md:!top-[53px] md:z-10 order-1 md:order-2">
      <div className="flex flex-row max-w-outerGrid mx-auto h-full">
        <div className="flex flex-col md:flex-row w-full">
          <div
            className={classNames(
              'w-full relative px-4 py-8 md:py-16 md:px-12 flex flex-col',
              {
                'md:w-1/2': !firstPillar,
              }
            )}
          >
            {!firstPillar && (
              <>
                <span className="bg-gradient-to-t from-5% from-blue to-red-dark absolute top-0 left-[-100vw] bottom-0 right-0 ">
                  &nbsp;
                </span>
                <Orb
                  pillarIndex={pillarIndexStore}
                  questionIndex={questionIndexStore}
                  className="!relative order-2 !inset-0 md:!absolute md:!inset-8 flex md:block items-center justify-center w-full py-8 md:py-0"
                  videoClassName="!max-w-[150px] !max-h-[150px] md:!max-w-[200px] md:!max-h-[200px] lg:!max-w-[350px] lg:!max-h-[350px] !translate-y-0 md:!-translate-y-1/2 !relative md:!absolute !top-0 md:!top-1/2 !left-0 md:!left-1/2 !translate-x-0 md:!-translate-x-1/2"
                />
              </>
            )}
            <div className="relative flex h-full order-1 overflow-hidden md:overflow-visible">
              <ol
                className={classNames(
                  'my-8 w-full border-t-4 md:border-t-0 md:border-l-4 border-blue-light/[.20] flex flex-row md:flex-col md:gap-8 text-xs text-white/[0.20] ml-[20px] md:ml-0 md:pb-4 md:pl-2 leading-none md:w-[55px] md:mt-0 md:absolute md:top-1/2 md:left-0 lg:-left-10 md:-left-20 md:-translate-y-1/2 mb-auto',

                  [pillarIndexStore === 0 ? 'hidden lg:flex' : 'flex']
                )}
              >
                {Array.from({ length: pillarsLengthStore }, (_, index) => {
                  const disabled = index <= pillarIndexStore ? false : true;

                  const backgroundColour = firstPillar ? 'grey' : 'blue';
                  return (
                    <PaginationItem
                      index={index}
                      backgroundColour={backgroundColour}
                      onClick={() => {
                        // This will take the user back to an earlier stage of the questionnaire
                        setPillarIndexStore(index);
                        setQuestionIndexStore(0);
                      }}
                      key={hash([index])}
                      disabled={disabled}
                      currentQuestion={currentQuestion}
                      questionsLength={questionsLength}
                      currentPillar={pillarIndexStore}
                    />
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
};

export default Pagination;
